/**
 * TrainingProvider — Phase 58.M (2026-08-29)
 *
 * Contesto globale del Training Gamificato:
 *  • carica lo stato da GET /api/training/me al login
 *  • traccia OGNI cambio di route (POST /api/training/track) finché la run
 *    è attiva → le missioni "visita" si completano navigando davvero il CRM
 *  • espone { state, refresh, tracking } a Sidebar, banner e TrainingPage
 *  • toast celebrativi quando una missione si completa
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from './api';
import { useAuth } from './auth-context';
import i18n from '../i18n';

const TrainingContext = createContext(null);

export function TrainingProvider({ children }) {
  const { user } = useAuth() || {};
  const loc = useLocation();
  const [state, setState] = useState(null);
  const trackingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) { setState(null); return null; }
    try {
      const { data } = await api.get('/training/me');
      setState(data);
      return data;
    } catch {
      return null;
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Route tracker: completa le missioni "visita" navigando davvero ──
  useEffect(() => {
    if (!user || !state) return;
    if (state.completed_at) return; // run chiusa → niente tracking
    if (trackingRef.current) return;
    trackingRef.current = true;
    api.post('/training/track', { path: loc.pathname })
      .then(({ data }) => {
        const newly = data?.newly_completed || [];
        newly.slice(0, 3).forEach((m) => {
          toast.success(i18n.t('trainingPage.missionCompleted', { defaultValue: 'Missione completata: {{title}}', title: m.title }), {
            description: i18n.t('trainingPage.pointsGained', { defaultValue: '+{{points}} punti', points: m.points }),
          });
        });
        if (data?.finished) {
          toast.success(i18n.t('trainingPage.trainingFinishedToast', 'Training completato! Hai ottenuto il Certificato HCF'), {
            description: i18n.t('trainingPage.trainingFinishedToastDesc', 'Il CRM è tutto tuo. Ottimo lavoro!'),
          });
        }
        if (newly.length || data?.finished) refresh();
        else if (data?.progress) {
          setState((s) => (s ? { ...s, progress: data.progress, locked: data.locked ?? s.locked } : s));
        }
      })
      .catch(() => {})
      .finally(() => { trackingRef.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, user?.id, state?.completed_at]);

  return (
    <TrainingContext.Provider value={{ state, refresh }}>
      {children}
    </TrainingContext.Provider>
  );
}

export const useTraining = () => useContext(TrainingContext) || { state: null, refresh: () => {} };
